/**
 * Organizations Admin — multi-tenant management
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, RefreshCw, Pencil, Trash2, Users, Building2, Check, X } from 'lucide-react';

const PLANS = ['trial', 'basic', 'pro', 'enterprise'];
const PLAN_COLORS = { trial: '#64748b', basic: '#007AFF', pro: '#34C759', enterprise: '#FF9500' };

function OrgRow({ org, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: org.name, plan: org.plan, maxUsers: org.maxUsers, maxChannels: org.maxChannels, active: org.active, accentColor: org.accentColor });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`/api/organizations/${org.id}`, form);
      onUpdate(res.data);
      setEditing(false);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  const rowStyle = { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,0,0,0.05)', marginBottom: 6 };

  if (editing) return (
    <div style={{ ...rowStyle, flexWrap: 'wrap', gap: 8 }}>
      <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
        style={{ flex: 1, minWidth: 120, padding: '4px 8px', background: '#fff', border: '1px solid rgba(0,122,255,0.30)', borderRadius: 6, color: '#e2e8f0', fontSize: 13 }} />
      <select value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}
        style={{ padding: '4px 8px', background: '#0f1629', border: '1px solid rgba(0,122,255,0.30)', borderRadius: 6, color: '#e2e8f0', fontSize: 12 }}>
        {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
      </select>
      <input type="number" value={form.maxUsers} onChange={e => setForm(f => ({ ...f, maxUsers: +e.target.value }))}
        style={{ width: 60, padding: '4px 6px', background: '#fff', border: '1px solid rgba(0,122,255,0.30)', borderRadius: 6, color: '#e2e8f0', fontSize: 12 }} placeholder="Users" />
      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#94a3b8', cursor: 'pointer' }}>
        <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} /> Activa
      </label>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={save} disabled={saving} style={{ padding: '4px 10px', background: 'rgba(52,199,89,0.20)', border: '1px solid rgba(0,255,136,0.3)', borderRadius: 6, color: '#34C759', fontSize: 12, cursor: 'pointer' }}>
          <Check size={12} />
        </button>
        <button onClick={() => setEditing(false)} style={{ padding: '4px 10px', background: 'rgba(255,55,95,0.12)', border: '1px solid rgba(255,55,95,0.20)', borderRadius: 6, color: '#FF375F', fontSize: 12, cursor: 'pointer' }}>
          <X size={12} />
        </button>
      </div>
    </div>
  );

  return (
    <div style={rowStyle}>
      <div style={{ width: 10, height: 10, borderRadius: '50%', background: org.active ? '#34C759' : '#FF375F', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>{org.name}</div>
        <div style={{ fontSize: 10, color: '#64748b' }}>/{org.slug}</div>
      </div>
      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: `${PLAN_COLORS[org.plan]}22`, color: PLAN_COLORS[org.plan], fontWeight: 700 }}>{org.plan.toUpperCase()}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748b' }}>
        <Users size={11} /> {org.userCount}/{org.maxUsers}
      </div>
      <button onClick={() => setEditing(true)} style={{ padding: '4px 8px', background: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,122,255,0.20)', borderRadius: 6, color: '#007AFF', fontSize: 12, cursor: 'pointer' }}>
        <Pencil size={12} />
      </button>
      <button onClick={() => onDelete(org.id)} style={{ padding: '4px 8px', background: 'rgba(255,55,95,0.10)', border: '1px solid rgba(255,55,95,0.20)', borderRadius: 6, color: '#FF375F', fontSize: 12, cursor: 'pointer' }}>
        <Trash2 size={12} />
      </button>
    </div>
  );
}

export default function OrganizationsAdmin() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', plan: 'trial', maxUsers: 10, maxChannels: 5 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/organizations');
      setOrgs(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post('/api/organizations', form);
      setOrgs(o => [...o, { ...res.data, userCount: 0, channelCount: 0 }]);
      setForm({ name: '', slug: '', plan: 'trial', maxUsers: 10, maxChannels: 5 });
      setCreating(false);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const deleteOrg = async (id) => {
    if (!confirm('¿Eliminar organización? Los usuarios quedarán sin org asignada.')) return;
    await axios.delete(`/api/organizations/${id}`);
    setOrgs(o => o.filter(x => x.id !== id));
  };

  const inputStyle = { padding: '6px 10px', background: '#fff', border: '1px solid rgba(0,122,255,0.20)', borderRadius: 8, color: '#e2e8f0', fontSize: 13, width: '100%' };

  return (
    <div style={{ padding: 24, maxWidth: 800 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Building2 size={20} style={{ color: '#007AFF' }} />
        <h1 style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0', margin: 0 }}>Organizaciones</h1>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={load} style={{ padding: '6px 10px', background: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,122,255,0.20)', borderRadius: 8, color: '#007AFF', cursor: 'pointer' }}>
            <RefreshCw size={13} />
          </button>
          <button onClick={() => setCreating(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: 'rgba(0,122,255,0.15)', border: '1px solid rgba(0,122,255,0.30)', borderRadius: 8, color: '#007AFF', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            <Plus size={13} /> Nueva org
          </button>
        </div>
      </div>

      {creating && (
        <form onSubmit={create} style={{ marginBottom: 16, padding: 16, background: '#F5F5F7', border: '1px solid rgba(0,122,255,0.15)', borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h3 style={{ margin: 0, fontSize: 13, color: '#007AFF', fontWeight: 700 }}>Nueva organización</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>Nombre</label>
              <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="K-G Seguridad" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>Slug (URL)</label>
              <input required value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))} placeholder="kg-seguridad" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>Plan</label>
              <select value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))} style={inputStyle}>
                {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>Máx. usuarios</label>
              <input type="number" value={form.maxUsers} onChange={e => setForm(f => ({ ...f, maxUsers: +e.target.value }))} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setCreating(false)} style={{ padding: '6px 14px', background: 'none', border: '1px solid rgba(100,116,139,0.3)', borderRadius: 8, color: '#64748b', cursor: 'pointer', fontSize: 12 }}>Cancelar</button>
            <button type="submit" style={{ padding: '6px 16px', background: 'rgba(0,122,255,0.20)', border: '1px solid rgba(0,122,255,0.40)', borderRadius: 8, color: '#007AFF', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Crear</button>
          </div>
        </form>
      )}

      {loading && <p style={{ fontSize: 12, color: '#64748b', textAlign: 'center', padding: 20 }}>Cargando…</p>}
      {orgs.map(org => (
        <OrgRow key={org.id} org={org}
          onUpdate={updated => setOrgs(o => o.map(x => x.id === updated.id ? { ...x, ...updated } : x))}
          onDelete={deleteOrg}
        />
      ))}
      {!loading && orgs.length === 0 && (
        <p style={{ fontSize: 12, color: '#475569', textAlign: 'center', padding: 20 }}>Sin organizaciones aún</p>
      )}
    </div>
  );
}
